import { useMemo } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Badge } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { ExpandablePanel } from "app/pages/activation/components/Shared/ExpandablePanel/ExpandablePanel";
import { ExpandablePanelVariant } from "app/pages/activation/components/Shared/ExpandablePanel/ExpandablePanel.types";
import { generateAutomationName } from "app/pages/activation/helpers/generateAutomationName";
import type { ActionableInsight } from "app/pages/activation/types/api";

import { InsightFeedbackCopyButton } from "./InsightFeedback/InsightFeedbackCopyButton";
import { InsightFeedbackThumbsGroup } from "./InsightFeedback/InsightFeedbackThumbsGroup";
import { InsightParagraph } from "./InsightParagraph";
import * as SC from "./styles";
import { INSIGHTS_SUGGESTIONS_SECTION_LABEL } from "./texts";

export type InsightRowProps = {
    insight: ActionableInsight;
    isOpen: boolean;
    onToggle: (open: boolean) => void;
    index: number;
};

export const InsightRow = ({ insight, isOpen, onToggle, index }: InsightRowProps) => {
    const { title, tag, description, suggestion } = insight;

    const titleContent = useMemo(
        () => (
            <Flex
                flex="1"
                alignItems="center"
                gap="8px"
                minWidth={0}
            >
                <Text
                    fontVariant="subtitle2"
                    color={colorsPalettes.carbon[400]}
                >
                    {title}
                </Text>

                <Badge
                    type="custom"
                    bgColor={colorsPalettes.sky[100]}
                    color={colorsPalettes.carbon[400]}
                    data-automation={generateAutomationName("home", ["insights", "row", String(index), "tag"])}
                >
                    <SC.TagBadgeLabel>{tag}</SC.TagBadgeLabel>
                </Badge>
            </Flex>
        ),
        [index, tag, title],
    );

    return (
        <ExpandablePanel
            variant={ExpandablePanelVariant.Nested}
            dataAutomation={generateAutomationName("home", ["insights", "row", String(index)])}
            title={titleContent}
            isOpen={isOpen}
            onToggle={onToggle}
        >
            <SC.RowBodyStack>
                <InsightParagraph text={description} />

                <Stack gap="8px">
                    <SC.SuggestionsSectionLabel>
                        {translate(INSIGHTS_SUGGESTIONS_SECTION_LABEL)}
                    </SC.SuggestionsSectionLabel>

                    <InsightParagraph text={suggestion} />
                </Stack>

                <SC.FeedbackToolbar
                    data-automation={generateAutomationName("home", ["insights", "row", String(index), "feedback"])}
                >
                    <InsightFeedbackCopyButton
                        insight={insight}
                        dataAutomation={generateAutomationName("home", [
                            "insights",
                            "row",
                            String(index),
                            "feedback",
                            "copy",
                        ])}
                    />

                    <InsightFeedbackThumbsGroup
                        insightIndex={index}
                        insight={insight}
                    />
                </SC.FeedbackToolbar>
            </SC.RowBodyStack>
        </ExpandablePanel>
    );
};
